import {
  contractTypeOptions,
  renewalIndicatorOptions,
} from "@/features/license-fee/list/utils/private-office-contract-mappings";
import { addYears } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import {
  PrivateOfficeContractAsset,
  PrivateOfficeContractAssetWithMetadata,
} from "@/services/private-office-contracts-service/types";

export const statusOptions = [
  {
    id: 1,
    slug: "waiting",
    name: "Aguardando",
  },
  {
    id: 2,
    slug: "active",
    name: "Ativo",
  },
  {
    id: 3,
    slug: "inactive",
    name: "Inativo",
  },
  {
    id: 4,
    slug: "canceled",
    name: "Cancelado",
  },
  {
    id: 5,
    slug: "notice",
    name: "Aviso prévio",
  },
];

export const getContractTypeLabel = (type: string) => {
  return contractTypeOptions.find((option) => option.value === type)?.label;
};

export const getRenewalIndicatorLabel = (renewalIndicator: string) => {
  return renewalIndicatorOptions.find(
    (option) => option.value === renewalIndicator,
  )?.label;
};

export const getNextBirthDate = (birthDate: string) => {
  const today = new Date();
  const start = new Date(birthDate);
  const yearDiff = today.getFullYear() - start.getFullYear();
  const nextDate = addYears(start, yearDiff);

  // If this year's anniversary already passed, the next one is a year later;
  // otherwise it's this year's upcoming anniversary (nextDate itself).
  if (nextDate < today) {
    return formatInTimeZone(addYears(nextDate, 1), "UTC", "dd/MM/yyyy");
  }

  return formatInTimeZone(nextDate, "UTC", "dd/MM/yyyy");
};

export const calculateAmountCharged = (
  monthlyCredits: number,
  pricePerCredit: number,
) => {
  return monthlyCredits * pricePerCredit;
};

export const calculateTransferAmount = (
  take: string,
  partnerPrice?: string,
) => {
  if (!partnerPrice) return 0;

  const takePercentage = parseFloat(take) / 100;
  const price = parseFloat(partnerPrice);

  return (1 - takePercentage) * price;
};

export const calculateMarginPercentage = (
  chargedAmount: number,
  transferAmount: number,
) => {
  if (chargedAmount === 0) return 0;
  const marginPercentage =
    ((chargedAmount - transferAmount) / chargedAmount) * 100;
  return Number(marginPercentage.toFixed(2));
};

export const calculateMarginValue = (
  chargedAmount: number,
  transferAmount: number,
) => {
  return chargedAmount - transferAmount;
};

export const getStatusData = (
  status: string,
): { bgColor: string; label: string } => {
  switch (status) {
    case "waiting":
      return { bgColor: "#FEF08A", label: "Aguardando" };
    case "active":
      return { bgColor: "#86EFAC", label: "Ativo" };
    case "expired":
      return { bgColor: "#D4D4D4", label: "Expirado" };
    case "canceled":
      return { bgColor: "#FCA5A5", label: "Cancelado" };
    case "inactive":
      return { bgColor: "#D4D4D4", label: "Inativo" };
    case "notice":
      return { bgColor: "#F97316", label: "Aviso prévio" };
    default:
      return { bgColor: "#F4F4F5", label: status };
  }
};

export const getContractAssetsParsed = (
  assets: PrivateOfficeContractAsset[],
) => {
  if (!assets?.length) {
    return [];
  }

  const uniquesCreatedAt = [
    ...new Set(assets.map((asset) => asset.createdAt.substring(0, 10))),
  ].sort();

  const allAssetsByDate: PrivateOfficeContractAssetWithMetadata[] = [];

  uniquesCreatedAt.forEach((createdAt) => {
    const assetsWithSameCreatedAt = assets.filter(
      (asset) => asset.createdAt.substring(0, 10) === createdAt,
    );

    const assetsWithSameCreatedAtParsed = assetsWithSameCreatedAt.map(
      (asset) => {
        return {
          ...asset,
          fileName: asset.url.split("/").pop() || null,
          created: formatInTimeZone(
            new Date(asset.createdAt),
            "UTC",
            "dd/MM/yyyy",
          ),
          createdAt: formatInTimeZone(new Date(createdAt), "UTC", "dd/MM/yyyy"),
        };
      },
    );

    allAssetsByDate.push({
      createdAt: formatInTimeZone(new Date(createdAt), "UTC", "dd/MM/yyyy"),
      assets: assetsWithSameCreatedAtParsed,
    });
  });

  const firstCompanyAssignmentTerm = allAssetsByDate[0]?.assets.find(
    (asset) => asset.type === "company_assignment_term",
  );
  const firstPartnerAssignmentTerm = allAssetsByDate[0]?.assets.find(
    (asset) => asset.type === "partner_assignment_term",
  );

  return {
    allAssetsByDate: allAssetsByDate || [],
    firstCompanyAssignmentTerm,
    firstPartnerAssignmentTerm,
  };
};
